'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { usePlpContext } from 'plp/context'
import { scrollLastProduct } from 'plp/hooks/useRestorePosition/scrollLastProduct'
import type { UseRestorePosition } from 'plp/hooks/useRestorePosition/useRestorePosition'
import type { VirtualGrid } from 'plp/types/Catalog'
import type { Product } from 'product/types'
import { applyStylesIf, cx } from 'utils/cx'

import { Cell } from './Cell/Cell'
import { GroupHeader } from './GroupHeader/GroupHeader'

import styles from './Grid.module.scss'

export interface GridProps {
	group: VirtualGrid
	catalogItemsData: Product[]
	showAsSmall: boolean
	count: number
	pendingScrollSlot: UseRestorePosition
}

/**
 * Based on default grid values for the grid
 */

export function Grid({
	catalogItemsData,
	group,
	showAsSmall,
	pendingScrollSlot,
}: GridProps): React.ReactNode {
	const { catalogId } = usePlpContext()
	const { isMobile } = useMasterData()

	return group.fromServer?.length || group.onClient.length ? (
		<>
			<GroupHeader group={group} />
			<ul className={styles.grid}>
				{group.fromServer?.map((item) => {
					const showAsFeatured =
						!isMobile && !!item.images.landscape && !showAsSmall
					const isEmpty = item.serverRender === null
					return (
						<li
							className={cx(
								applyStylesIf(showAsFeatured, styles.featured),
								applyStylesIf(isEmpty, styles.empty)
							)}
							key={item.id}
							data-slot={item.id}
							data-from-server='true'
							id={`p-${item.id}`}
							ref={(ref) => {
								if (ref && item.id === pendingScrollSlot.id) {
									scrollLastProduct(ref, catalogId)
								}
							}}
						>
							{item.serverRender}
						</li>
					)
				})}
				{group.onClient.slice(0, group.countToRender).map((item) => {
					const showAsFeatured =
						!isMobile && !!item.images.landscape && !showAsSmall
					return (
						<Cell
							key={item.id}
							className={cx(styles.cell, showAsFeatured && styles.featured)}
							priority={false}
							pendingScrollSlot={pendingScrollSlot}
							item={item}
							initialProductData={catalogItemsData.find(
								(product) => item.productId === product.id
							)}
							isTwoColumnSize={!!item.images.landscape}
						/>
					)
				})}
			</ul>
		</>
	) : null
}
